import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchBox } from '@/components';
import { useCompanyLevels } from '@/features/companies/hooks';
import { useCountries } from '@/features/country/hooks/useCountries';
import { CountryFlagIcon } from '@/features/country/countryIcon';
import { useTargetCompanies, useTargetCompaniesCount } from '../hooks';
import type { TargetCompany, TargetCompanyQueryParams } from '../types';
import TableLoadingView from '@/components/tables/TableLoadingView';
import EmptyTableView from '@/components/tables/EmptyTableView';
import {
  Dropdown,
  DropdownTriggerButton,
  DropdownContent,
  DropdownCheckboxItem,
  DropdownEmpty,
  DropdownLoading,
} from '@/components/Dropdown';
import useDebounce from '@/utils/useDebounce';
import AssignmentContentContainer from './assignmentContainer';

interface SelectTargetCompanyStepProps {
  sourceCompanyId: string;
  selectedCompany: TargetCompany | null;
  onCompanySelect: (company: TargetCompany | null) => void;
}

export function SelectTargetCompanyStep({
  sourceCompanyId,
  selectedCompany,
  onCompanySelect,
}: SelectTargetCompanyStepProps) {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([]);

  const debouncedSearch = useDebounce(searchInput);

  const { data: companyLevels, isLoading: isLoadingLevels } =
    useCompanyLevels(sourceCompanyId);
  const { data: countriesData, isLoading: isLoadingCountries } = useCountries();
  const countries = countriesData?.countries || [];

  const queryParams: TargetCompanyQueryParams = useMemo(
    () => ({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      sourceCompanyId,
      levels: selectedLevels.length > 0 ? selectedLevels : undefined,
      countryCodes: selectedCountryCodes.length > 0 ? selectedCountryCodes : undefined,
    }),
    [page, pageSize, debouncedSearch, sourceCompanyId, selectedLevels, selectedCountryCodes]
  );

  const countParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      sourceCompanyId,
      levels: selectedLevels.length > 0 ? selectedLevels : undefined,
      countryCodes: selectedCountryCodes.length > 0 ? selectedCountryCodes : undefined,
    }),
    [debouncedSearch, sourceCompanyId, selectedLevels, selectedCountryCodes]
  );

  const { data: companiesData, isLoading: isLoadingCompanies, isFetching } = useTargetCompanies(
    queryParams,
    !!sourceCompanyId
  );
  const { data: countData } = useTargetCompaniesCount(countParams, !!sourceCompanyId);

  const companies = companiesData?.data || [];
  const total = Math.max((countData?.total || 0) - 1, 0);

  const handleLevelChange = useCallback((levelId: string, checked: boolean) => {
    setSelectedLevels((prev) =>
      checked ? [...prev, levelId] : prev.filter((id) => id !== levelId)
    );
    setPage(1);
  }, []);

  const handleCountryChange = useCallback((countryCode: string, checked: boolean) => {
    setSelectedCountryCodes((prev) =>
      checked ? [...prev, countryCode] : prev.filter((code) => code !== countryCode)
    );
    setPage(1);
  }, []);

  const handleCompanyClick = useCallback(
    (company: TargetCompany) => {
      if (selectedCompany?.id === company.id) {
        onCompanySelect(null);
      } else {
        onCompanySelect(company);
      }
    },
    [selectedCompany, onCompanySelect]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  return (
    <AssignmentContentContainer
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    >
      <div className="flex flex-wrap gap-2">
        {/* Level Filter */}
        <Dropdown>
          <DropdownTriggerButton>
            {t('deviceAssignment.step2.levelFilter')}
            {selectedLevels.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">
                {selectedLevels.length}
              </span>
            )}
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </DropdownTriggerButton>
          <DropdownContent>
            {isLoadingLevels ? (
              <DropdownLoading rows={3} />
            ) : !companyLevels || companyLevels.length === 0 ? (
              <DropdownEmpty>{t('deviceAssignment.step2.noLevels')}</DropdownEmpty>
            ) : (
              companyLevels.map((level) => (
                <DropdownCheckboxItem
                  key={level.id}
                  checked={selectedLevels.includes(level.id)}
                  onChange={(checked) => handleLevelChange(level.id, checked)}
                >
                  {level.name}
                </DropdownCheckboxItem>
              ))
            )}
          </DropdownContent>
        </Dropdown>

        {/* Country Filter */}
        <Dropdown>
          <DropdownTriggerButton>
            {t('deviceAssignment.step2.countryFilter')}
            {selectedCountryCodes.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">
                {selectedCountryCodes.length}
              </span>
            )}
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </DropdownTriggerButton>
          <DropdownContent>
            {isLoadingCountries ? (
              <DropdownLoading rows={3} />
            ) : !countries || countries.length === 0 ? (
              <DropdownEmpty>{t('deviceAssignment.step2.noCountries')}</DropdownEmpty>
            ) : (
              countries.map((country) => (
                <DropdownCheckboxItem
                  key={country.code}
                  checked={selectedCountryCodes.includes(country.code)}
                  onChange={(checked) => handleCountryChange(country.code, checked)}
                >
                  {/* <span className="mr-2">{getFlagEmoji(country.code)}</span> */}
                  <CountryFlagIcon country={country} />
                  <span className="ml-2">{country.name}</span>
                </DropdownCheckboxItem>
              ))
            )}
          </DropdownContent>
        </Dropdown>

        {/* Search */}
        <SearchBox
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('deviceAssignment.step2.searchPlaceholder')}
          ariaLabel={t('deviceAssignment.step2.searchPlaceholder')}
          clearAriaLabel={t('deviceAssignment.step2.searchPlaceholder')}
        />
      </div>

      {/* Company List */}
      <div className="overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb">
        {isFetching && !isLoadingCompanies && (
          <div className="absolute top-2 right-2 z-10">
            <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}

        {isLoadingCompanies ? (
          <TableLoadingView isLoading={true} rows={5} columns={3} withHeader={false} />
        ) : companies.length === 0 ? (
          <EmptyTableView />
        ) : (
          <div className="space-y-2 h-full">
            {companies.filter((company) => company.id !== sourceCompanyId).map((company) => {
              return <TargetCompany key={company.id} company={company} handleCompanyClick={handleCompanyClick} selectedCompany={selectedCompany} />
            })}

           {/*  {Array.from({ length: 20 }, (_, i) => {
              const testCompany: TargetCompany = {
                id: `test-company-${i + 1}`,
                name: `Test Company ${i + 1}`,
                country: 'Germany',
                countryCode: 'DE',
                place: `Test City ${i + 1}`,
                level: 1,
              };
              return (
                <TargetCompany
                  key={testCompany.id}
                  company={testCompany}
                  handleCompanyClick={handleCompanyClick}
                  selectedCompany={selectedCompany}
                />
              );
            })} */}

          </div>
        )}
      </div>
    </AssignmentContentContainer>
  );
}


function TargetCompany({ company, handleCompanyClick, selectedCompany }: { company: TargetCompany; handleCompanyClick: (company: TargetCompany) => void; selectedCompany: TargetCompany | null }) {
  return <button
    key={company.id}
    type="button"
    onClick={() => handleCompanyClick(company)}
    className={`w-full p-3 rounded-lg border text-left transition-colors ${selectedCompany?.id === company.id
      ? 'border-primary bg-primary/5'
      : 'border-border hover:border-primary/50 hover:bg-surface-darker'}`}
  >
    <div className="flex items-center gap-3 align-center">
      <CountryFlagIcon country={{ code: company.countryCode, name: company.country }} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-onsurface truncate">{company.name}</div>
        <div className="text-sm text-muted-foreground truncate">{company.place}</div>
      </div>
      {selectedCompany?.id === company.id && (
        <svg
          className="shrink-0 size-5 text-primary"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </div>
  </button>;
}
